import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Header from '../shared/TailwindComponents/Header';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import ProfileImage from '../Header/ProfileImage';
import { fetchAllLawyers } from '../../store/actions/LawyerAction';
import { useInView } from 'react-intersection-observer';
import { normalise } from '../shared/access/accessModel';
import Skeleton from '../shared/TailwindComponents/Skeleton';

interface Lawyer {
  LawyerID: string;
  FirstName: string;
  LastName: string;
  Email: string;
  UserTypeName: string;
  ProfilePicture: string;
  HourlyRate?: number;
  ExperienceYears?: number;
  Role: string;
  Specializations: string[];
  OrganizationID?: string;
}

const renderSpecializations = (specializations: string[]) => {
  if (!specializations || specializations.length === 0) return null;
  const visibleSpecs = specializations.slice(0, 2);
  const hiddenCount = specializations.length - 2;
  return (
    <div className="flex flex-wrap gap-1">
      {visibleSpecs.map((spec, index) => (
        <Badge key={index} color="indigo">{spec}</Badge>
      ))}
      {hiddenCount > 0 && (
        <Badge color="gray">+{hiddenCount}</Badge>
      )}
    </div>
  );
};

const LawyerGridItem: React.FC<{ lawyer: Lawyer; onClick: () => void }> = ({ lawyer, onClick }) => {
  const { t } = useTranslation();
  const fullName = `${lawyer.FirstName} ${lawyer.LastName}`.trim() || t('No name');
  const initials = `${lawyer.FirstName?.[0] || ''}${lawyer.LastName?.[0] || ''}`.toUpperCase();

  return (
    <div
      className="glass relative cursor-pointer rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col gap-3"
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        <ProfileImage profilePicture={normalise(lawyer).avatar} email={lawyer.Email} initials={initials} name={fullName} />
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{fullName}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{lawyer.Email}</p>
        </div>
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {lawyer.Role} {lawyer.OrganizationID ? `${t('at Organization')} ${lawyer.OrganizationID}` : ''}
      </div>
      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
        {lawyer.ExperienceYears && (
          <div>
            <span>{lawyer.ExperienceYears} {t('years experience')}</span>
          </div>
        )}
        {lawyer.HourlyRate && (
          <div>
            <span>${lawyer.HourlyRate}/{t('hour')}</span>
          </div>
        )}
      </div>
      {lawyer.Specializations && lawyer.Specializations.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {renderSpecializations(lawyer.Specializations)}
        </div>
      )}
    </div>
  );
};

const Lawyers: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const { allLawyers } = useSelector((state: RootState) => state.lawyer);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);

  const [isLawyersLoading, setIsLawyersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [sortValue, setSortValue] = useState('name:asc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalLawyers, setTotalLawyers] = useState(0);
  const [selectedLawyers, setSelectedLawyers] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { ref, inView } = useInView({ threshold: 0.5 });

  const fetchInitialLawyers = useCallback(async () => {
    setIsLawyersLoading(true);
    const [sortBy, sortOrder] = sortValue.split(':');
    const response = await dispatch(fetchAllLawyers({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: 1,
      pageSize: 12,
    }));
    if (response && typeof response === 'object') {
      const {  currentPage, total_lawyers, hasMore } = response.payload;
      setPage(currentPage);
      setTotalLawyers(total_lawyers);
      setHasMore(hasMore);
    }
    setIsLawyersLoading(false);
  }, [dispatch, searchQuery, selectedFilters, sortValue]);

  const loadMoreLawyers = useCallback(async () => {
    if (!hasMore || isLawyersLoading) return;
    setIsLawyersLoading(true);
    const scrollPosition = scrollRef.current?.scrollTop || 0;
    const [sortBy, sortOrder] = sortValue.split(':');
    const response = await dispatch(fetchAllLawyers({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: page + 1,
      pageSize: 12,
    }));
    if (response && typeof response === 'object') {
      const { currentPage, total_lawyers, hasMore } = response.payload;
      setPage(currentPage);
      setTotalLawyers(total_lawyers);
      setHasMore(hasMore);
    }
    setIsLawyersLoading(false);
    if (scrollRef.current) scrollRef.current.scrollTop = scrollPosition;
  }, [dispatch, page, searchQuery, selectedFilters, sortValue, hasMore, isLawyersLoading]);

  useEffect(() => {
    setPage(1);
    fetchInitialLawyers();
  }, [searchQuery, selectedFilters, sortValue, fetchInitialLawyers]);

  useEffect(() => {
    if (inView && hasMore && !isLawyersLoading) loadMoreLawyers();
  }, [inView, hasMore, isLawyersLoading, loadMoreLawyers]);

  const handleLawyerClick = (lawyer: Lawyer) => history.push(`/lawyers/${lawyer.LawyerID}`);
  const handleSearchChange = (query: string) => setSearchQuery(query);
  const handleFilterChange = (filterName: string, selectedValues: string[]) =>
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  const handleSortChange = (value: string) => setSortValue(value);

  const handleSelectLawyer = (lawyerId: string) => {
    setSelectedLawyers((prev) =>
      prev.includes(lawyerId) ? prev.filter((id) => id !== lawyerId) : [...prev, lawyerId]
    );
  };

  const handleSelectAllLawyers = () => {
    setSelectedLawyers(
      selectedLawyers.length === allLawyers.length && allLawyers.length > 0
        ? []
        : allLawyers.map((lawyer: Lawyer) => lawyer.LawyerID)
    );
  };

  const filters = [
    {
      id: 'LawyerType',
      name: t('Lawyer Type'),
      options: [
        { value: 'IndependentLawyer', label: t('Independent Lawyer') },
        { value: 'LawFirmAdmin', label: t('Law Firm Admin') },
        { value: 'LawFirmEmployee', label: t('Law Firm Employee') },
      ],
    },
  ];

  const sortOptions = [
    { value: 'name:asc', label: t('Name Ascending'), current: true },
    { value: 'name:desc', label: t('Name Descending'), current: false },
    { value: 'created_at:asc', label: t('Created At Ascending'), current: false },
    { value: 'created_at:desc', label: t('Created At Descending'), current: false },
    { value: 'email:asc', label: t('Email Ascending'), current: false },
    { value: 'email:desc', label: t('Email Descending'), current: false },
  ];

  const columns = [t('Profile'), t('Role'), t('Experience'), t('Hourly Rate'), t('Specializations')];

  const tableData = allLawyers.map((lawyer: Lawyer) => {
    const fullName = `${lawyer.FirstName} ${lawyer.LastName}`.trim() || t('No name');
    const initials = `${lawyer.FirstName?.[0] || ''}${lawyer.LastName?.[0] || ''}`.toUpperCase();
    return {
      id: lawyer.LawyerID,
      [t('Profile')]: {
        name: fullName,
        email: lawyer.Email,
        profilePicture: normalise(lawyer).avatar,
        initials: initials,
      },
      [t('Role')]: lawyer.Role,
      [t('Experience')]: lawyer.ExperienceYears ? `${lawyer.ExperienceYears} ${t('years')}` : t('N/A'),
      [t('Hourly Rate')]: lawyer.HourlyRate ? `$${lawyer.HourlyRate}/${t('hour')}` : t('N/A'),
      [t('Specializations')]: renderSpecializations(lawyer.Specializations),
    };
  });

  return (
    <div className="bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300 flex flex-col h-full overflow-hidden px-1">
      <div className="transition-all duration-200 bg-background-light dark:bg-background-dark">
        <Header subscriptionActive={subscriptionActive} />
        <FilterBarWithTabs
          itemCount={allLawyers.length}
          totalItemCount={totalLawyers || allLawyers.length}
          itemCountLabel="lawyers"
          filters={filters}
          sortOptions={sortOptions}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onSearchChange={handleSearchChange}
          sortValue={sortValue}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />
      </div>
      {/*
        THE ONE PAGE THAT SCROLLS AS A PAGE, and deliberately so.

        Every other list gives the scroller to the table. This one hangs an
        infinite-scroll sentinel BELOW the table (`ref={ref}`, `useInView`), so
        the scroller has to be an ancestor of both — a sentinel inside the
        table's own box would never come into view. The table carries
        `max-h-none`, which means its inner box has no height constraint and so
        never becomes a scroller itself: there is exactly ONE scrollbar here,
        this one, and the sticky head sticks to it.

        Do not "fix" this to match the others without first moving the sentinel
        onto `Table`'s `hasMore`/`onLoadMore` — which trades infinite scroll for
        a Load More button, a UX change and not a layout one.
      */}
      <div className="flex-grow overflow-y-auto custom-scrollbar-main" ref={scrollRef}>
        {isLawyersLoading && allLawyers.length === 0 ? (
          <Skeleton variant={viewMode === 'grid' ? 'grid' : 'table'} rows={3} />
        ) : (
          <div className="animate-fade-in">
            {viewMode === 'grid' ? (
              <div className="px-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2 pb-20">
                  {allLawyers.map((lawyer: Lawyer) => (
                    <LawyerGridItem
                      key={lawyer.LawyerID}
                      lawyer={lawyer}
                      onClick={() => handleLawyerClick(lawyer)}
                    />
                  ))}
                  {isLawyersLoading && <Skeleton variant="grid" rows={3} />}
                  {hasMore && <div ref={ref} className="h-16 col-span-full" />}
                </div>
              </div>
            ) : (
              <div className="px-4">
                {allLawyers.length > 0 ? (
                  <Table
                    columns={columns}
                    data={tableData}
                    selectedIds={selectedLawyers}
                    onSelect={handleSelectLawyer}
                    onSelectAll={handleSelectAllLawyers}
                    onRowClick={(row) => history.push(`/lawyers/${row.id}`)}
                    total_items={totalLawyers || allLawyers.length}
                    total_showing_items={allLawyers.length}
                    setIsShowing={true}
                    showingLabel={t('lawyers')}
                    /*
                      The PAGE scrolls here, not the table — the infinite-scroll
                      sentinel below sits in the outer scroller, so the table has
                      to render at its full height for the sentinel to ever come
                      into view. Table's default `max-h-[calc(100vh-150px)]` would
                      cap it and open a second scroll region inside the first.
                    */
                    max="max-h-none"
                    min="min-h-0"
                  />
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <Badge color="gray">{t('No lawyers available')}</Badge>
                  </div>
                )}
                {hasMore && <div ref={ref} className="h-16" />}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Lawyers;
